import api from '@/api/axios';

const state = {
  isLoading: false,
  error: null,
  currentTaskId: null,
  lastGenerateResponse: null,
  // 태스크 상태 관련
  taskStatus: null,  // { task_id, status, progress, current_step, ... }
  isPolling: false,
  // 보고서 컨텐츠
  reportContent: null,  // 마크다운 형식의 보고서 내용
  reportS3Path: null,
};

const mutations = {
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_CURRENT_TASK_ID(state, taskId) {
    state.currentTaskId = taskId;
  },
  SET_LAST_GENERATE_RESPONSE(state, response) {
    state.lastGenerateResponse = response;
  },
  CLEAR_STATE(state) {
    state.isLoading = false;
    state.error = null;
    state.currentTaskId = null;
    state.lastGenerateResponse = null;
    state.taskStatus = null;
    state.isPolling = false;
    state.reportContent = null;
    state.reportS3Path = null;
  },
  SET_TASK_STATUS(state, status) {
    state.taskStatus = status;
  },
  SET_POLLING(state, isPolling) {
    state.isPolling = isPolling;
  },
  SET_REPORT_CONTENT(state, { content, s3Path }) {
    state.reportContent = content;
    state.reportS3Path = s3Path;
  },
  CLEAR_REPORT_CONTENT(state) {
    state.reportContent = null;
    state.reportS3Path = null;
  },
};

const actions = {
  /**
   * 보고서 생성 요청 API
   * @param {Object} payload
   * @param {number} [payload.project_id] - 프로젝트 ID (선택)
   * @param {number[]} payload.job_ids - crawling_job ID 리스트 (필수)
   * @param {string[]} payload.selected_sections - 생성할 섹션 코드 리스트 (필수)
   * @param {string} [payload.start_date] - 시작일 YYYY-MM-DD (선택)
   * @param {string} [payload.end_date] - 종료일 YYYY-MM-DD (선택)
   * @param {string[]} [payload.channels] - 채널 필터 (선택)
   * @param {string} [payload.model_type] - "anthropic" 또는 "openai" (기본값: anthropic)
   * @param {string} [payload.report_title] - 보고서 제목 (선택)
   */
  async generateReport({ commit, rootState }, payload) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const token = rootState.auth.token;
      if (!token) throw new Error('No authentication token found');

      // 필수 값 검증
      if (!payload.job_ids || payload.job_ids.length === 0) {
        throw new Error('Job ID가 필요합니다.');
      }
      if (!payload.selected_sections || payload.selected_sections.length === 0) {
        throw new Error('최소 하나의 섹션을 선택해주세요.');
      }

      // 요청 바디 구성
      const requestBody = {
        job_ids: payload.job_ids,
        selected_sections: payload.selected_sections,
        model_type: payload.model_type || 'anthropic',
      };

      // 선택적 파라미터 추가
      if (payload.project_id) {
        requestBody.project_id = payload.project_id;
      }
      if (payload.start_date) {
        requestBody.start_date = payload.start_date;
      }
      if (payload.end_date) {
        requestBody.end_date = payload.end_date;
      }
      if (payload.channels && payload.channels.length > 0) {
        requestBody.channels = payload.channels;
      }
      if (payload.report_title) {
        requestBody.report_title = payload.report_title;
      }

      const { data } = await api.post('/api/v1/reports/generate', requestBody);

      if (data.success) {
        commit('SET_CURRENT_TASK_ID', data.task_id);
        commit('SET_LAST_GENERATE_RESPONSE', data);
        return data;
      } else {
        throw new Error(data.message || '보고서 생성 요청에 실패했습니다.');
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        '보고서 생성 중 오류가 발생했습니다.';
      console.error('Failed to generate report:', error);
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // 상태 초기화
  clearReportState({ commit }) {
    commit('CLEAR_STATE');
  },

  /**
   * 보고서 생성 작업 상태 조회 API
   * @param {string} taskId - 조회할 task ID
   */
  async getTaskStatus({ commit, rootState }, taskId) {
    try {
      const token = rootState.auth.token;
      if (!token) throw new Error('No authentication token found');

      const { data } = await api.get(`/api/v1/reports/tasks/${taskId}/status`);
      commit('SET_TASK_STATUS', data);
      return data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        '상태 조회 중 오류가 발생했습니다.';
      console.error('Failed to get task status:', error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 폴링 시작 - 2초 간격으로 상태 조회
   * @param {string} taskId - 조회할 task ID
   * @param {Function} onComplete - 완료 시 콜백
   * @param {Function} onError - 에러 시 콜백
   */
  startPolling({ commit, dispatch, state }, { taskId, onComplete, onError }) {
    if (state.isPolling) return;

    commit('SET_POLLING', true);

    const poll = async () => {
      if (!state.isPolling) return;

      try {
        const status = await dispatch('getTaskStatus', taskId);

        if (status.status === 'completed') {
          commit('SET_POLLING', false);
          if (onComplete) onComplete(status);
        } else if (status.status === 'failed') {
          commit('SET_POLLING', false);
          if (onError) onError(status.error_message || '보고서 생성에 실패했습니다.');
        } else {
          // pending 또는 processing - 계속 폴링
          setTimeout(poll, 2000);
        }
      } catch (error) {
        commit('SET_POLLING', false);
        if (onError) onError(error.message);
      }
    };

    poll();
  },

  // 폴링 중지
  stopPolling({ commit }) {
    commit('SET_POLLING', false);
  },

  /**
   * 보고서 다운로드 API
   * @param {string} taskId - 다운로드할 task ID
   */
  async downloadReport({ commit, rootState }, taskId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const token = rootState.auth.token;
      if (!token) throw new Error('No authentication token found');

      const { data } = await api.get(`/api/v1/reports/tasks/${taskId}/download`);

      commit('SET_REPORT_CONTENT', {
        content: data.content,
        s3Path: data.s3_path
      });

      return data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        '보고서 다운로드 중 오류가 발생했습니다.';
      console.error('Failed to download report:', error);
      commit('SET_ERROR', errorMessage);
      throw new Error(errorMessage);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // 보고서 컨텐츠 초기화
  clearReportContent({ commit }) {
    commit('CLEAR_REPORT_CONTENT');
  },

  /**
   * 보고서 생성 내역 조회 API (프로젝트별 태스크 목록)
   * GET /api/v1/reports/projects/{project_id}/tasks?limit=20
   * @param {number} projectId - 프로젝트 ID (필수)
   * @param {number} [limit=20] - 조회 개수 제한
   * @returns {Promise<{tasks: Array, total: number}>}
   */
  async getReportTaskList({ rootState }, { projectId, limit = 20 }) {
    try {
      const token = rootState.auth.token;
      if (!token) throw new Error('No authentication token found');

      if (!projectId) {
        throw new Error('프로젝트 ID가 필요합니다.');
      }

      const { data } = await api.get(`/api/v1/reports/projects/${projectId}/tasks`, {
        params: { limit }
      });

      return {
        tasks: data.tasks || [],
        total: data.total || 0
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        '보고서 내역 조회 중 오류가 발생했습니다.';
      console.error('Failed to get report task list:', error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 보고서 생성 내역 조회 API (하위 호환성 유지)
   * @deprecated getReportTaskList 사용 권장
   */
  async getReportHistory({ dispatch }, projectId) {
    const result = await dispatch('getReportTaskList', { projectId });
    return result.tasks;
  },

  /**
   * Word 파일 다운로드 API (Presigned URL 방식)
   * @param {string} taskId - 다운로드할 task ID
   */
  async downloadWordReport({ rootState }, taskId) {
    try {
      const token = rootState.auth.token;
      if (!token) throw new Error('No authentication token found');

      const { data } = await api.get(`/api/v1/reports/tasks/${taskId}/download/word`);

      // Presigned URL로 직접 다운로드
      if (data.download_url) {
        const link = document.createElement('a');
        link.href = data.download_url;
        link.setAttribute('download', '');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return data;
      } else {
        throw new Error('다운로드 URL을 받지 못했습니다.');
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        'Word 파일 다운로드 중 오류가 발생했습니다.';
      console.error('Failed to download Word report:', error);
      throw new Error(errorMessage);
    }
  },
};

const getters = {
  isLoading: state => state.isLoading,
  error: state => state.error,
  currentTaskId: state => state.currentTaskId,
  lastGenerateResponse: state => state.lastGenerateResponse,
  taskStatus: state => state.taskStatus,
  isPolling: state => state.isPolling,
  reportContent: state => state.reportContent,
  reportS3Path: state => state.reportS3Path,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
